import { BottomBarController } from "./BottomBarController";
import { EMMIconName, usePopups } from "@mm-frontend/mm-web-ui-kit";
import { ERouteName } from "~/app/router/routeName.enum";
import { BottomBarItemModal, BottomBarItemNavigator } from "./BottomBarItem";
import { EBottomBarItem } from "../models/bottomBar.model";
import { UserModal } from "../ui/modals/user";
import { MobileCatalogModal } from "../ui/modals/mobileCatalog";
import { watchThrottled } from "@vueuse/core";
import { useAppSearchBounding } from "~/features/app-search"; 
import { useBottomBarSize } from "./useBottomBarSize";
import { ref } from 'vue';

const { createPopup } = usePopups();

const userPopup = createPopup({
  component: UserModal,
});

const catalogPopup = createPopup({
  component: MobileCatalogModal,
});

const controller = new BottomBarController();
const items = computed(() => controller.getItems());
const activeItemName = computed(() => controller.activeItemName);

function handleClick(item: BottomBarItemModal | BottomBarItemNavigator) {
  controller.handleClick(item);
}

function openModalByHash(hash: string) {
  controller.openModalByHash(hash);
}

function closeBottomBarPopups() {
  controller.closeBottomBarPopups();
}

const BOTTOM_BAR_TELEPORT_ID = 'BOTTOM_BAR_TARGET';
const BOTTOM_BAR_TELEPORT_SELECTOR = `[data-teleport-id="${BOTTOM_BAR_TELEPORT_ID}"]`;
  
const { searchOffsetFromTop } = useAppSearchBounding();
const { bottomBarHeight } = useBottomBarSize();

watchThrottled(
  () => [bottomBarHeight.value, searchOffsetFromTop.value],
  ([bottom, top]) => { 
    catalogPopup.updateOptions({
      withoutOverlay: true,
      spacingBottom: bottom,
      spacingTop: top,
    })

    userPopup.updateOptions({
      withoutOverlay: true,
      spacingBottom: bottom,
    })
  },
  { throttle: 1000 }
);

const activeModalName = computed(() => controller.activeModalName);
const activeNavigatorName = computed(() => controller.activeNavigatorName);
const isCatalogModalOpened = computed(() => activeModalName.value === EBottomBarItem.CATALOG);
const isUserModalOpened = computed(() => activeModalName.value === EBottomBarItem.USER);

export const useBottomBar = () => {
  const router = useRouter();

  const tapBarHeight = ref(0);

  if (controller.getItems().length === 0) {
    controller.setItems([
      new BottomBarItemNavigator({
        name: EBottomBarItem.HOME,
        title: 'Главная',
        icon: EMMIconName['home-outlined'],
        router,
        navigateTo: { name: ERouteName.MAIN },
        activeRoutes: [ERouteName.MAIN],
      }),
      new BottomBarItemModal({
        name: EBottomBarItem.CATALOG,
        title: 'Каталог',
        icon: EMMIconName['shopping-catalog'],
        router,
        popup: catalogPopup,
        activeRoutes: [ERouteName.CATEGORY, ERouteName.PRODUCT, ERouteName.SHOP, ERouteName.SEARCH],
      }),
      new BottomBarItemNavigator({
        name: EBottomBarItem.CART,
        title: 'Корзина',
        icon: EMMIconName['cart'],
        router,
        navigateTo: { name: ERouteName.CART },
        activeRoutes: [ERouteName.CART],
      }),
      new BottomBarItemNavigator({
        name: EBottomBarItem.FAVORITE,
        title: 'Избранное',
        icon: EMMIconName['heart'],
        router,
        navigateTo: { name: ERouteName.FAVORITES },
        activeRoutes: [ERouteName.FAVORITES],
      }),
      new BottomBarItemModal({
        name: EBottomBarItem.USER,
        title: 'Профиль',
        icon: EMMIconName['user'],
        router,
        popup: userPopup,
        activeRoutes: [ERouteName.USER, ERouteName.USER_ORDERS, ERouteName.USER_SETTINGS, ERouteName.USER_COMPANY, ERouteName.USER_COMPANIES, ERouteName.USER_ACCOUNT],
      })
    ])
  }

  return {
    items,
    activeModalName,
    activeNavigatorName,
    activeItemName,
    handleClick,
    closeBottomBarPopups,
    openModalByHash,
    BOTTOM_BAR_TELEPORT_ID,
    BOTTOM_BAR_TELEPORT_SELECTOR,
    
    isCatalogModalOpened,
    isUserModalOpened,
    tapBarHeight
  }
}